import { LockOutlined, MailOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  Flex,
  Form,
  FormProps,
  Input,
  Layout,
  Row,
  Spin,
  Typography,
} from 'antd';
import RizeWhiteLogo from 'assets/SVG/rize-logo-white.svg';
import RizeLogo from 'assets/SVG/rize-logo.svg';
import { useAnalytics } from 'context/AnalyticsContext';
import { usePermissions } from 'context/PermissionsContext';
import useRouteConfig from 'hooks/useRouteConfig';
import useVerifyWithTokenAndRedirect from 'hooks/useVerifyWithToken';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { AppDispatch } from 'store';
import { login } from 'store/auth/action';
import { resetState } from 'store/auth/index';
import { selectLoginError, selectToken } from 'store/auth/selectors';
import UserPermissions from 'types/rbac';
import './styles.scss';

type LoginPayload = {
  emailId: string;
  password: string;
};

const Login = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isErrorLogin, setIsErrorLogin] = useState(false);
  const [prevValue, setPrevValue] = useState({ emailId: '', password: '' });

  const { state } = useLocation();
  const [searchParams] = useSearchParams();
  const { analytics } = useAnalytics();

  const [form] = Form.useForm<LoginPayload>();
  const emailId = Form.useWatch('emailId', form);
  const password = Form.useWatch('password', form);

  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const loginError = useSelector(selectLoginError);
  const { getUserAccessibleRedirectLocation } = useRouteConfig();

  const { updatePermissions } = usePermissions();

  const token = useSelector(selectToken);
  const nextLocation = state?.from || getUserAccessibleRedirectLocation();

  const tokenFromUrl = searchParams.get('token');
  const redirectUri = searchParams.get('redirectUri');

  const handleLogin: FormProps<LoginPayload>['onFinish'] = async (value) => {
    setIsLoading(true);
    const response = await dispatch(login(value));
    if (login.fulfilled.match(response)) {
      await updatePermissions(
        response?.payload?.permissions as UserPermissions,
      );
      analytics?.track('User Login', {
        id: response?.payload?.user?.id,
        email: response?.payload?.user?.email,
      });
    }
    setIsLoading(false);
  };

  useVerifyWithTokenAndRedirect(
    tokenFromUrl!,
    redirectUri || getUserAccessibleRedirectLocation()!,
  );

  useEffect(() => {
    if (!token || !!tokenFromUrl) return;
    setIsLoading(false);
    return navigate(nextLocation);
  }, [dispatch, navigate, token, nextLocation, tokenFromUrl]);

  useEffect(() => {
    if (loginError) {
      dispatch(resetState());
      setIsErrorLogin(true);
      setPrevValue({
        emailId: emailId,
        password: password,
      });
      form.setFields([
        {
          name: 'emailId',
          errors: [''],
        },
        {
          name: 'password',
          errors: [''],
        },
      ]);
    }
  }, [loginError]);

  const resetErrorFields = () => {
    if (isErrorLogin) {
      setIsErrorLogin(false);
      form.setFields([
        {
          name: 'emailId',
          errors: [],
        },
        {
          name: 'password',
          errors: [],
        },
      ]);
    }
  };

  const isFormValid = form
    .getFieldsError()
    .every(({ errors }) => errors.length === 0);

  if (token && !tokenFromUrl) {
    return <Spin className="m-auto" />; // To avoid flicker of login form
  }

  if (tokenFromUrl) {
    return (
      <Layout className="d-flex login-container" style={{ height: '100vh' }}>
        <Flex
          gap={5}
          vertical
          justify="center"
          align="center"
          className="m-auto"
        >
          <img src={RizeLogo} alt="Rize Logo" />
          <Typography.Text>
            Please wait while we redirect you to console.
          </Typography.Text>
          <Spin />
        </Flex>
      </Layout>
    );
  }

  return (
    <Layout className="login-container">
      <Row>
        <Col className="sidebar" xs={0} lg={8}>
          <img src={RizeWhiteLogo} alt="Rize Logo - White" />
        </Col>
        <Col className="form-container" xs={24} lg={16}>
          <Form form={form} onFinish={handleLogin} onChange={resetErrorFields}>
            <Flex vertical justify="center" align="center">
              <img src={RizeLogo} alt="Rize Logo" />
              <Typography.Title>Welcome to Rize Console!</Typography.Title>
              <Typography.Text>
                Please enter credentials below to sign in to your account.
              </Typography.Text>
              <Form.Item
                name="emailId"
                required
                rules={[
                  {
                    required: true,
                    message: 'Please enter your email address',
                  },
                  {
                    type: 'email',
                    message: 'Please enter a valid email address',
                  },
                ]}
              >
                <Input prefix={<MailOutlined />} placeholder="Email ID" />
              </Form.Item>
              <Form.Item
                name="password"
                required
                rules={[
                  { required: true, message: 'Please enter your password' },
                  { min: 8, message: 'Required minimum 8 Characters.' },
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Password"
                />
              </Form.Item>
              {isErrorLogin && (
                <Typography.Text className="error-message" type="danger">
                  Invalid credentials. Please try again or contact your manager
                  to create account.
                </Typography.Text>
              )}
              <Form.Item>
                <Button
                  disabled={
                    !isFormValid ||
                    (prevValue.emailId === emailId &&
                      prevValue.password === password)
                  }
                  type="primary"
                  htmlType="submit"
                  loading={isLoading}
                >
                  Sign In
                </Button>
              </Form.Item>
            </Flex>
          </Form>
        </Col>
      </Row>
    </Layout>
  );
};

export default Login;
